import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_GLOBAL_HTTP_ERROR_UI } from '@core/services/http/http-context.tokens';

export interface IdoSaleDto {
  idoSaleId: number;
  projectId: number;
  blockchainId: number;
  tokenId?: number | null;
  tokenAddress?: string | null;
  contractAddress: string;
  saleStart: string;
  publicSaleStart: string;
  saleEnd: string;
  maxPerBuyer: string;
  priceTokensPerEth: string;
  capTotal: string;
  issued: string;
  status: string;
  createdAt: string;
  updatedAt?: string | null;
  buyers?: IdoBuyerDto[] | null;
}

export interface IdoBuyerDto {
  idoBuyerId: number;
  idoSaleId: number;
  address: string;
  purchased: string;
  claimed: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateIdoSaleRequest {
  projectId: number;
  blockchainId: number;
  tokenId?: number | null;
  tokenAddress?: string | null;
  contractAddress: string;
  saleStart: string;
  publicSaleStart: string;
  saleEnd: string;
  maxPerBuyer?: string;
  priceTokensPerEth: string;
  capTotal: string;
  status?: string;
}

/**
 * Hand client until OpenAPI regen includes IdoSale (MVP-112).
 */
@Injectable({ providedIn: 'root' })
export class IdoSaleApiClient {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.dataApiUrl}/data-api/IdoSale`;

  /** Avoid global 404→/not-found redirects when a project has no sale yet (or API lag). */
  private readonly skipGlobalErrorUi = new HttpContext().set(
    SKIP_GLOBAL_HTTP_ERROR_UI,
    true,
  );

  listByProject(projectId: number): Observable<IdoSaleDto[]> {
    return this.http.get<IdoSaleDto[]>(`${this.base}/by-project/${projectId}`, {
      context: this.skipGlobalErrorUi,
    });
  }

  list(options?: {
    blockchainId?: number;
    evmChainId?: number;
    status?: string;
  }): Observable<IdoSaleDto[]> {
    const params: Record<string, string> = {};
    if (options?.blockchainId != null) {
      params['blockchainId'] = String(options.blockchainId);
    }
    if (options?.evmChainId != null) {
      params['evmChainId'] = String(options.evmChainId);
    }
    if (options?.status) {
      params['status'] = options.status;
    }
    return this.http.get<IdoSaleDto[]>(this.base, {
      params,
      context: this.skipGlobalErrorUi,
    });
  }

  getByAddress(address: string): Observable<IdoSaleDto> {
    return this.http.get<IdoSaleDto>(
      `${this.base}/by-address/${encodeURIComponent(address)}`,
      { context: this.skipGlobalErrorUi },
    );
  }

  getById(id: number, includeBuyers = false): Observable<IdoSaleDto> {
    return this.http.get<IdoSaleDto>(`${this.base}/${id}`, {
      params: { includeBuyers: String(includeBuyers) },
      context: this.skipGlobalErrorUi,
    });
  }

  create(request: CreateIdoSaleRequest): Observable<IdoSaleDto> {
    return this.http.post<IdoSaleDto>(this.base, request, {
      context: this.skipGlobalErrorUi,
    });
  }
}
